import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Planet, House, Plus, Stack, Gavel, BookmarkSimple, Database, Archive, UsersThree, SignOut, Sun, Moon,
} from "@phosphor-icons/react";

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
        isActive ? "bg-blue-500/10 text-blue-300 border-l-2 border-blue-500 pl-[10px]" : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border-l-2 border-transparent"
      }`
    }
  >
    <Icon size={18} weight="duotone" />
    <span>{label}</span>
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const isEditor = user?.role === "editor" || user?.role === "admin";

  return (
    <div className="h-screen flex bg-[#0B0E14] overflow-hidden">
      <aside className="w-60 shrink-0 border-r border-white/5 flex flex-col bg-[#0B0E14]">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/5">
          <Planet size={26} weight="duotone" className="text-blue-500" />
          <div className="leading-tight">
            <div className="font-semibold tracking-tight text-sm">Observatory</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Editorial Workflow</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <NavItem to="/" icon={House} label="Dashboard" testid="nav-dashboard" />
          <NavItem to="/collect" icon={Plus} label="Collect Entry" testid="nav-collect" />
          <NavItem to="/input" icon={Stack} label="Input Database" testid="nav-input" />
          {isEditor && <NavItem to="/review" icon={Gavel} label="Editorial Review" testid="nav-review" />}
          <div className="pt-3 mt-3 border-t border-white/5">
            <p className="px-3 pb-1 font-mono text-[10px] uppercase tracking-wider text-slate-600">Repositories</p>
            <NavItem to="/editorial" icon={BookmarkSimple} label="Editorial Folder" testid="nav-editorial" />
            <NavItem to="/working" icon={Database} label="Reference DB" testid="nav-working" />
            <NavItem to="/archive" icon={Archive} label="Archive" testid="nav-archive" />
          </div>
          {user?.role === "admin" && (
            <div className="pt-3 mt-3 border-t border-white/5">
              <NavItem to="/admin" icon={UsersThree} label="Users" testid="nav-admin" />
            </div>
          )}
        </nav>
        <div className="border-t border-white/5 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center font-medium text-sm">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{user?.role}</div>
            </div>
          </div>
          <Button variant="ghost" data-testid="theme-toggle" onClick={toggle}
            className="w-full justify-start gap-2 text-slate-400 hover:text-slate-100 hover:bg-white/5">
            {theme === "dark" ? <Sun size={18} weight="duotone" /> : <Moon size={18} weight="duotone" />}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </Button>
          <Button variant="ghost" data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full justify-start gap-2 text-slate-400 hover:text-red-300 hover:bg-red-500/10 mt-1">
            <SignOut size={18} weight="duotone" /> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
